import React, { useContext, useState, useEffect, useReducer, useRef, useMemo } from 'react'
import { useAtom } from 'jotai'
import dayjs from 'dayjs'
import Slider from 'react-slick'
import { useIntl } from 'react-intl'

import { userMenuAtom } from '~atoms'
import { useMediaCardData } from '~hooks'
import { getUser, getAgeGroupSlug, isRelatedArticle, hasBeenSeen } from '~utilities'

import { MediaCard } from '~components/shared'
import { ageGroups } from '~data/age-groups'

const Recommended = ({ currentArticle, topics, type = null, topicPage = false }) => {
  const _ = require(`lodash`)
  const [isUserMenuOpen] = useAtom(userMenuAtom)
  const [, forceUpdate] = useReducer(x => x + 1, 0)
  useEffect(() => {
    forceUpdate()
  }, [isUserMenuOpen])

  const { messages } = useIntl()
  const [isClient, setIsClient] = useState(false)
  const [shuffledArticles, setShuffledArticles] = useState([])

  const user = getUser()
  const children = user && user?.['custom:childrenDOBs'] ? JSON.parse(user['custom:childrenDOBs']) : null
  const interests = user && user?.['custom:topicInterests'] ? JSON.parse(user['custom:topicInterests']) : null
  const articlesSeen = user && user?.['custom:articlesSeen'] ? JSON.parse(user['custom:articlesSeen']) : null

  const articles = useMediaCardData()
  let initialArticles = articles

  let relatedType = []
  let carouselTitle = ''

  switch (type) {
    case 'Activity':
      relatedType = ['Activity', 'Book']
      carouselTitle = messages['more-activities']
      break
    case 'Book':
      relatedType = ['Activity', 'Book']
      carouselTitle = messages['more-books']
      break
    case 'Video':
      relatedType = ['Video']
      carouselTitle = messages['more-books']
      break
    default:
      carouselTitle = messages['recommended-articles']
      break
  }

  // never recommend the current article and filter according to type for secondary carousels
  if (currentArticle && type) {
    initialArticles = articles
      .filter(article => article.contentful_id !== currentArticle)
      .filter(article => relatedType.includes(article.type))
  } else if (currentArticle) {
    initialArticles = articles.filter(article => article.contentful_id !== currentArticle)
  }

  // construct arrays of page "ages" and page "topics" based on the pages's primary topic slugs
  let pageAgeGroups = []
  let pageTopics = []
  const splitTopics = topic => {
    const { slug } = topic
    if (ageGroups.includes(slug)) {
      pageAgeGroups = pageAgeGroups.concat([slug])
    } else {
      pageTopics = pageTopics.concat([slug])
    }
  }
  if (topics) {
    topics.map(topic => splitTopics(topic))
  }

  let filteredArticles = initialArticles

  // filter recommended articles to only include those with related age group
  if (pageAgeGroups.length > 0) {
    filteredArticles = filteredArticles.filter(article => isRelatedArticle(article, pageAgeGroups))
  }

  // filter recommended articles to only include those with related topic
  if (pageTopics.length > 0) {
    filteredArticles = filteredArticles.filter(article => isRelatedArticle(article, pageTopics))
  }

  if (topicPage && children && children.length > 0) {
    let userAgeGroups = []
    children.map(child => {
      let ageGroup = dayjs().diff(child, 'months') |> getAgeGroupSlug
      userAgeGroups = userAgeGroups.concat([ageGroup])
    })
    filteredArticles = filteredArticles.filter(article => isRelatedArticle(article, userAgeGroups))
  }

  if (topicPage && interests && interests.length > 0) {
    let userTopics = []
    interests.map(interest => {
      userTopics = userTopics.concat([interest])
    })
    filteredArticles = filteredArticles.filter(article => isRelatedArticle(article, userTopics))
  }

  // filter recomended articles to include only unseen articles
  if (articlesSeen && articlesSeen.length > 0) {
    filteredArticles = filteredArticles.filter(article => !hasBeenSeen(article, articlesSeen))
  }

  const slider = useRef(null)
  const settings = useMemo(() => {
    return {
      arrows: false,
      dots: false,
      infinite: true,
      speed: 400,
      slidesToShow: 3,
      slidesToScroll: 3,
      easing: 'ease',
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1
          }
        }
      ]
    }
  }, [])

  const headline = (
    <div className='headline-section my-16'>
      <div>{user ? messages['top-picks-for-you'] : carouselTitle}</div>
      <hr aria-hidden />
    </div>
  )

  // shuffle articles on React rehydration
  useEffect(() => {
    const shuffleAndSupplementArticles = (filtered, initial) => {
      if (filtered.length >= 9) {
        setShuffledArticles(_.shuffle(filtered).slice(0, 9))
      } else {
        setShuffledArticles(
          _.shuffle(filtered)
            .concat(_.shuffle(initial.slice(0, 50)))
            .slice(0, 9)
        )
      }
    }
    shuffleAndSupplementArticles(filteredArticles, initialArticles)
    setIsClient(true)
  }, [isUserMenuOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isClient) {
    return (
      <div>
        {headline}
        <div className='w-full -mx-4'>
          <Slider ref={slider} {...settings}>
            {shuffledArticles.map(({ id, image, title, tags, url, type }) => (
              <div key={id} className='flex lg:w-1/3 p-4'>
                <MediaCard image={image} title={title} tags={tags} url={url} type={type} />
              </div>
            ))}
          </Slider>
          {shuffledArticles.length > 3 && (
            <div className='flex justify-end items-center my-8'>
              <div>
                <button
                  onClick={() => slider.current.slickPrev()}
                  className='icon-large bg-green border border-green hover:bg-white text-white group'
                >
                  <em className='fas fa-caret-left group-hover:text-green' />
                </button>
                <button
                  onClick={() => slider.current.slickNext()}
                  className='icon-large ml-2 bg-green border border-green hover:bg-white text-white group'
                >
                  <em className='fas fa-caret-right group-hover:text-green' />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  } else {
    return (
      <div>
        {headline}
        <div className='w-full -mx-4'>
          <div className='flex'>
            {initialArticles.slice(0, 3).map(({ id, image, title, tags, url, type }) => (
              <div key={id} className='flex lg:w-1/3 p-4'>
                <MediaCard image={image} title={title} tags={tags} url={url} type={type} />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }
}

export default Recommended
